/**
 * OrderListPage — 주문 목록 (검색 + 테이블).
 *
 * 【역할】
 * - 주문 마스터 검색/조회 전용 페이지 (수정은 OrderFormPage 에서)
 * - 행 더블클릭 → OrderFormPage 수정 모드 (?id=xxx)
 *
 * 【주요 기능】
 * - 검색 (기간 + 고객명 + 상태 + 결제수단)
 * - 상태/결제수단 코드 Tag 표시
 * - AppPage (SearchPanel + Toolbar 통합 레이아웃)
 * - 행 더블클릭 라우팅 → /orders/form?id={orderId}
 *
 * 【사용 훅】
 * - useFramework / useDataSet
 * - useLabel / useCodes / useMessage / useI18nTickStore
 *
 * 【Backend RPC serviceName】
 * - order/searchList  (action=search) — 주문 목록 조회
 *
 * 【DataSet 구조】
 * - ds_list: 주문 목록 (PK: orderId, 조회 전용)
 *
 * 【사용처】 router/index.tsx — `{ path: 'order' }`
 * 【포팅 원본】 vue-spring-fw/frontend/src/pages/PageOrderList.vue
 */
import { useEffect, useMemo } from 'react'
import { Form, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useNavigate } from 'react-router-dom'
import dayjs, { Dayjs } from 'dayjs'
import AppPage from '@/components/common/AppPage'
import DataTable from '@/components/common/DataTable'
import FormSelect from '@/components/common/FormSelect'
import FormInput from '@/components/common/FormInput'
import { FormDateRangePicker } from '@/components/common/FormDatePicker'
import { useFramework, useDataSet } from '@/framework'
import { useLabel } from '@/framework/useLabel'
import { useCodes } from '@/framework/useCodes'
import { useMessage } from '@/framework/useMessage'
import { useI18nTickStore } from '@/framework/useLocale'
import { fmtDate } from '@/utils/format'

interface OrderRow {
  orderId: string
  orderDate: string
  customerId: string
  customerName: string
  totalAmount: number
  orderStatus: string
  paymentMethod: string
  deliveryAddress: string
  remark: string
}

function statusColor(s: string): string {
  switch (s) {
    case 'ORDER':
      return 'blue'
    case 'CONFIRM':
    case 'SHIPPING':
      return 'orange'
    case 'DELIVERED':
      return 'green'
    case 'CANCEL':
      return 'red'
    default:
      return 'default'
  }
}

function formatCurrency(v: number): string {
  if (v == null) return '0'
  return new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(v)
}

/**
 * OrderListPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function OrderListPage() {
  const navigate = useNavigate()
  const { $transaction, loading } = useFramework()
  const { t } = useLabel()
  const { getCodeName, getOptions, loadCodes } = useCodes()
  const msg = useMessage()
  useI18nTickStore((s) => s.tick)
  const listDs = useDataSet<OrderRow>([])
  const [form] = Form.useForm()

  useEffect(() => {
    loadCodes(['ORDER_STATUS', 'PAYMENT_METHOD'])
  }, [loadCodes])

  const STATUS_OPTIONS = useMemo(() => getOptions('ORDER_STATUS'), [getOptions])

  useEffect(() => {
    form.setFieldsValue({
      dateRange: [dayjs().subtract(30, 'day'), dayjs()],
      keyword: '',
      customerName: '',
      orderStatus: []
    })
    fn_search()
  }, [])

  const fn_search = async () => {
    const v = form.getFieldsValue()
    const [dateFrom, dateTo] = (v.dateRange ?? []) as [Dayjs, Dayjs]
    if (dateFrom && dateTo && dateFrom.isAfter(dateTo)) {
      msg.byId('MSG_INVALID_DATE_RANGE')
      return
    }
    await $transaction({
      id: 'select',
      service: 'order/searchList',
      action: 'search',
      inds: {
        ds_search: {
          dateFrom: dateFrom?.format('YYYY-MM-DD'),
          dateTo: dateTo?.format('YYYY-MM-DD'),
          keyword: v.keyword,
          customerName: v.customerName,
          orderStatus: v.orderStatus
        }
      },
      outds: { ds_order: listDs }
    })
  }

  const fn_reset = () => {
    form.resetFields()
    form.setFieldsValue({
      dateRange: [dayjs().subtract(30, 'day'), dayjs()],
      orderStatus: []
    })
  }

  const columns: ColumnsType<OrderRow> = useMemo(
    () => [
      { title: t('COL_ORDER_ID', '주문번호'), dataIndex: 'orderId', width: 160 },
      {
        title: t('COL_ORDER_DATE', '주문일'),
        dataIndex: 'orderDate',
        width: 110,
        render: (v) => fmtDate(v)
      },
      { title: t('COL_CUSTOMER_NAME', '고객명'), dataIndex: 'customerName', width: 160 },
      {
        title: t('COL_AMOUNT', '금액'),
        dataIndex: 'totalAmount',
        width: 140,
        align: 'right',
        render: (v) => formatCurrency(v)
      },
      {
        title: t('COL_ORDER_STATUS', '상태'),
        dataIndex: 'orderStatus',
        width: 110,
        render: (s: string) => (
          <Tag color={statusColor(s)}>{getCodeName('ORDER_STATUS', s) || s}</Tag>
        )
      },
      {
        title: t('COL_PAYMENT_METHOD', '결제'),
        dataIndex: 'paymentMethod',
        width: 100,
        render: (v: string) => getCodeName('PAYMENT_METHOD', v) || v
      },
      { title: t('COL_DELIVERY_ADDRESS', '배송주소'), dataIndex: 'deliveryAddress' },
      { title: t('COL_REMARK', '비고'), dataIndex: 'remark', width: 200 }
    ],
    [t, getCodeName]
  )

  const dataSource = listDs.getVisibleRows().map((r) => ({ ...r.data, key: r._rowId }))

  return (
    <AppPage
      search={{
        form,
        onSearch: fn_search,
        onReset: fn_reset,
        loading,
        content: (
          <>
            <Form.Item name="dateRange" label={t('LBL_ORDER_DATE', '기간')}>
              <FormDateRangePicker />
            </Form.Item>
            <Form.Item name="keyword" label={t('LBL_KEYWORD', '키워드')}>
              <FormInput placeholder={t('PH_ORDER_NO', '주문번호')} style={{ width: 180 }} />
            </Form.Item>
            <Form.Item name="customerName" label={t('LBL_CUSTOMER_NAME', '고객명')}>
              <FormInput placeholder={t('PH_CUSTOMER_SEARCH', '고객명')} style={{ width: 160 }} />
            </Form.Item>
            <Form.Item name="orderStatus" label={t('LBL_ORDER_STATUS', '상태')}>
              <FormSelect
                mode="multiple"
                options={STATUS_OPTIONS}
                style={{ width: 220 }}
                placeholder={t('PH_ALL', '전체')}
              />
            </Form.Item>
          </>
        )
      }}
      toolbar={{
        onSearch: fn_search,
        onAdd: () => navigate('/sales/order/form'),
        onExport: () => msg.byId('MSG_NOT_IMPLEMENTED'),
        permissions: { R: true, C: true, E: true }
      }}
    >
      <DataTable<OrderRow>
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        onRow={(rec) => ({
          onDoubleClick: () => navigate(`/sales/order/form?id=${rec.orderId}`)
        })}
      />
    </AppPage>
  )
}
